import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatRoas } from "@/lib/formatters";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function DaypartingHeatmap({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-breakdown-dayparting", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("breakdown/dayparting", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const rows = data || [];
  const maxSpend = Math.max(...rows.map((r: { spend: number }) => r.spend), 1);
  const sorted = [...rows].sort((a: { roas: number }, b: { roas: number }) => b.roas - a.roas);
  const bestHours = sorted.slice(0, 3);

  const getIntensity = (spend: number) => {
    const norm = maxSpend > 0 ? spend / maxSpend : 0;
    const alpha = 0.08 + norm * 0.6;
    return `rgba(99, 102, 241, ${alpha})`;
  };

  const empty = !isLoading && !error && rows.length === 0;

  return (
    <DashboardWidget title="Dayparting Heatmap" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#8C8578" }} data-testid="empty-dayparting">No dayparting data available</div>
      ) : (
        <div className="px-4 pb-3 h-full flex flex-col" data-testid="chart-dayparting">
          {bestHours.length > 0 && (
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {bestHours.map((h: { hourLabel: string; roas: number }) => (
                <span key={h.hourLabel} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                  {h.hourLabel} ({formatRoas(h.roas)})
                </span>
              ))}
            </div>
          )}
          <div className="flex-1 overflow-auto">
            <div className="grid grid-cols-8 gap-1">
              {rows.map((r: { hour: number; hourLabel: string; spend: number; roas: number; ctr: number }) => (
                <div
                  key={r.hour}
                  className="rounded text-center py-2 cursor-default"
                  style={{ backgroundColor: getIntensity(r.spend) }}
                  title={`${r.hourLabel}: ${formatCurrency(r.spend)} spend, ${formatRoas(r.roas)} ROAS`}
                >
                  <p className="text-[9px] font-medium text-gray-500">{r.hourLabel}</p>
                  <p className="text-[10px] font-bold text-gray-800">{formatCurrency(r.spend)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
